import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { useMutation } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { trackEvent } from "@/hooks/usePageTracking";

const newsletterSchema = z.object({
  name: z.string().max(100, "Name must be less than 100 characters").optional().or(z.literal("")),
  email: z.string().email("Invalid email address").max(255, "Email must be less than 255 characters"),
  gdpr_consent: z.boolean().refine((val) => val === true, {
    message: "You must accept the GDPR consent",
  }),
});

type NewsletterFormValues = z.infer<typeof newsletterSchema>;

interface NewsletterFormProps {
  compact?: boolean;
  onSuccess?: () => void;
}

export const NewsletterForm = ({ compact = false, onSuccess }: NewsletterFormProps) => {
  const { i18n } = useTranslation();

  const form = useForm<NewsletterFormValues>({
    resolver: zodResolver(newsletterSchema),
    defaultValues: {
      name: "",
      email: "",
      gdpr_consent: false,
    },
  });

  const submitNewsletter = useMutation({
    mutationFn: async (data: NewsletterFormValues) => {
      // Save to database
      const { error: dbError } = await supabase.from("leads").insert([
        {
          name: data.name || null,
          email: data.email,
          source: "newsletter",
          gdpr_consent: data.gdpr_consent,
        },
      ]);

      if (dbError) throw dbError;

      // Send welcome email
      const { data: emailData, error: emailError } = await supabase.functions.invoke(
        "send-newsletter-email",
        {
          body: {
            name: data.name,
            email: data.email,
            language: i18n.language,
          },
        }
      );

      if (emailError) {
        console.error("Email error:", emailError);
      }

      return emailData;
    },
    onSuccess: () => {
      toast.success(
        i18n.language === 'dk' 
          ? "Tak for din tilmelding! Tjek din email." 
          : "Thank you for subscribing! Check your email."
      );
      form.reset();
      onSuccess?.();
      
      // Track newsletter signup
      trackEvent('newsletter_signup', {
        source: 'newsletter_form'
      });
    },
    onError: (error: any) => {
      console.error('Newsletter signup error:', error);
      toast.error(
        i18n.language === 'dk' 
          ? "Der opstod en fejl. Prøv venligst igen." 
          : "An error occurred. Please try again."
      );
    },
  });

  const handleSubmit = (data: NewsletterFormValues) => {
    submitNewsletter.mutate(data);
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
        {!compact && (
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{i18n.language === 'dk' ? 'Navn' : 'Name'}</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        )}

        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Email *</FormLabel>
              <FormControl>
                <Input type="email" {...field} placeholder={i18n.language === 'dk' ? 'din@email.dk' : 'your@email.com'} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="gdpr_consent"
          render={({ field }) => (
            <FormItem className="flex flex-row items-start space-x-3 space-y-0">
              <FormControl>
                <Checkbox checked={field.value} onCheckedChange={field.onChange} />
              </FormControl>
              <div className="space-y-1 leading-none">
                <FormLabel className="text-sm font-normal">
                  {i18n.language === 'dk' 
                    ? 'Jeg accepterer behandling af mine personlige data *'
                    : 'I accept processing of my personal data *'}
                </FormLabel>
                <FormMessage />
              </div>
            </FormItem>
          )}
        />

        <Button type="submit" disabled={submitNewsletter.isPending} className="w-full">
          {submitNewsletter.isPending 
            ? (i18n.language === 'dk' ? 'Tilmelder...' : 'Subscribing...') 
            : (i18n.language === 'dk' ? 'Tilmeld' : 'Subscribe')}
        </Button>
      </form>
    </Form>
  );
};
